import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Web as WebIcon,
  Storage as StorageIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  Payment,
  Timeline,
  Apps as AppsIcon,
  AdminPanelSettings as AdminIcon,
  Business as OrgIcon,
  Receipt as ReceiptIcon,
  DataUsage as DataUsageIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import appLogo from "../../assets/logo.png";
import { isAdmin } from "../../utils/roleUtils";
import { SPACING, BORDER_RADIUS, ICON_SIZES } from "../../theme/constants";
import { flexCenter } from "../../theme/responsive";
import OrgSwitcher from "../org/OrgSwitcher";

const menuItems = [
  { text: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
  { text: "WP Sites", icon: <WebIcon />, path: "/dashboard/sites" },
  { text: "Docker Apps", icon: <AppsIcon />, path: "/dashboard/applications" },
  { text: "Backups", icon: <StorageIcon />, path: "/dashboard/backups" },
  { text: "Activities", icon: <Timeline />, path: "/dashboard/activities" },
  { text: "Analytics", icon: <AssessmentIcon />, path: "/dashboard/analytics" },
  { text: "Organization", icon: <OrgIcon />, path: "/dashboard/org/settings" },
  {
    text: "Import Site",
    icon: <CloudUploadIcon />,
    path: "/dashboard/import-site",
  },
  { text: "Plans", icon: <Payment />, path: "/dashboard/plans" },
  { text: "Billing", icon: <ReceiptIcon />, path: "/dashboard/billing" },
  { text: "Usage", icon: <DataUsageIcon />, path: "/dashboard/usage" },
  { text: "Settings", icon: <SettingsIcon />, path: "/dashboard/settings" },
  {
    text: "Admin Panel",
    icon: <AdminIcon />,
    path: "/admin",
    adminOnly: true,
  },
];

export default function Sidebar({ user, isMobile, onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const userIsAdmin = isAdmin(user);

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile && onNavigate) onNavigate();
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden", // prevent outer scroll
      }}
    >
      {/* ── Fixed: Logo ── */}
      <Box
        sx={{
          p: SPACING.md,
          py: SPACING.sm,
          display: "flex",
          alignItems: "center",
          gap: SPACING.sm,
          minHeight: { xs: 48, sm: 48 },
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: ICON_SIZES.xxl,
            height: ICON_SIZES.xxl,
            borderRadius: BORDER_RADIUS.sm,
            ...flexCenter,
            flexShrink: 0,
            px: 2,
          }}
        >
          <img
            src={appLogo}
            alt="WP Spark Logo"
            style={{ width: ICON_SIZES.xxxl, height: ICON_SIZES.xxxl }}
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, lineHeight: 1.2 }}
          >
            WP Spark
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.6875rem" }}
          >
            WP & Docker Apps Hosting
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      {/* ── Fixed: Org Switcher ── */}
      <Box sx={{ flexShrink: 0 }}>
        <OrgSwitcher />
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      {/* ── Scrollable: Navigation List ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          // thin custom scrollbar
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "divider",
            borderRadius: 2,
          },
          "&::-webkit-scrollbar-thumb:hover": { bgcolor: "text.disabled" },
        }}
      >
        <List sx={{ px: SPACING.sm, py: SPACING.xs }}>
          {menuItems.map((item) => {
            if (item.adminOnly && !userIsAdmin) return null;

            const isActive = location.pathname === item.path;

            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    borderRadius: 1,
                    py: 0.75,
                    backgroundColor: isActive ? "primary.main" : "transparent",
                    color: isActive ? "white" : "text.primary",
                    "&:hover": {
                      backgroundColor: isActive
                        ? "primary.dark"
                        : "action.hover",
                    },
                    transition: "all 0.15s",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? "white" : "text.secondary",
                      minWidth: 32,
                      "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: "0.8125rem",
                      fontWeight: isActive ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      {/* ── Fixed: User Info ── */}
      <Box sx={{ p: 1.5, flexShrink: 0 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "grey.800" : "grey.50",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: "primary.main",
              fontSize: "0.8125rem",
              flexShrink: 0,
            }}
          >
            {user?.firstName?.[0] || "U"}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: "0.8125rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.6875rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
              }}
            >
              {user?.email}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
